// User Search Service - Search for public user profiles

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  DocumentSnapshot,
  DocumentData,
} from '@firebase/firestore';
import { db } from './firebase';
import {
  PublicUserProfile,
  PrivacySettings,
  DEFAULT_PRIVACY_SETTINGS,
} from '../types';

const COLLECTION_NAME = 'userProfiles';

/**
 * Remove undefined values from an object (Firestore doesn't accept undefined)
 */
function removeUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined)
  ) as Partial<T>;
}

/**
 * Get or create a user profile
 */
export async function getOrCreateUserProfile(
  userId: string,
  displayName: string,
  photoURL?: string
): Promise<PublicUserProfile> {
  const docRef = doc(db, COLLECTION_NAME, userId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as PublicUserProfile;
  }

  // Create new profile with default privacy settings
  const newProfile: Record<string, unknown> = {
    displayName: displayName || 'Anonymer Nutzer',
    privacy: DEFAULT_PRIVACY_SETTINGS,
    joinedAt: Timestamp.now(),
    reviewCount: 0,
    favoriteCount: 0,
  };

  // Only add photoURL if it exists
  if (photoURL) newProfile.photoURL = photoURL;

  await setDoc(docRef, newProfile);
  return { id: userId, ...newProfile } as PublicUserProfile;
}

/**
 * Get a user profile by ID
 */
export async function getUserProfile(userId: string): Promise<PublicUserProfile | null> {
  const docRef = doc(db, COLLECTION_NAME, userId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return { id: docSnap.id, ...docSnap.data() } as PublicUserProfile;
}

/**
 * Update user profile
 */
export async function updateUserProfile(
  userId: string,
  updates: Partial<Omit<PublicUserProfile, 'id'>>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, userId);
  // Remove undefined values - Firestore doesn't accept undefined
  const cleanUpdates = Object.fromEntries(
    Object.entries(updates).filter(([_, value]) => value !== undefined)
  );
  await updateDoc(docRef, cleanUpdates);
}

/**
 * Update privacy settings
 */
export async function updatePrivacySettings(
  userId: string,
  privacy: PrivacySettings
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, userId);
  await updateDoc(docRef, { privacy });
}

/**
 * Search for users by display name (only searchable users)
 */
export async function searchUsers(
  searchTerm: string,
  maxResults: number = 20
): Promise<PublicUserProfile[]> {
  if (!searchTerm || searchTerm.length < 2) {
    return [];
  }

  // Firestore doesn't support full-text search, so we do prefix matching
  const searchLower = searchTerm.toLowerCase();
  const searchEnd = searchLower + '\uf8ff';

  const q = query(
    collection(db, COLLECTION_NAME),
    where('privacy.searchable', '==', true),
    where('displayNameLower', '>=', searchLower),
    where('displayNameLower', '<=', searchEnd),
    limit(maxResults)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as PublicUserProfile[];
}

/**
 * Get all searchable users (for browsing)
 */
export async function getSearchableUsers(
  maxResults: number = 50
): Promise<PublicUserProfile[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('privacy.searchable', '==', true),
    orderBy('displayName'),
    limit(maxResults)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as PublicUserProfile[];
}

/**
 * Get user's public reviews (if privacy allows)
 */
export async function getUserPublicReviews(
  userId: string
): Promise<{ placeId: string; placeName: string; rating: number; comment: string }[]> {
  const profile = await getUserProfile(userId);

  if (!profile || !profile.privacy.showReviews) {
    return [];
  }

  // Fetch reviews from reviews collection
  const reviewsQuery = query(
    collection(db, 'reviews'),
    where('userId', '==', userId),
    where('status', '!=', 'hidden'),
    orderBy('status'),
    orderBy('createdAt', 'desc'),
    limit(20)
  );

  const snapshot = await getDocs(reviewsQuery);
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      placeId: data.placeId,
      placeName: data.placeName || 'Unbekannter Ort',
      rating: data.rating,
      comment: data.comment,
    };
  });
}

/**
 * Ensure displayNameLower field exists for search
 */
export async function ensureSearchableField(
  userId: string,
  displayName: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, userId);
  await updateDoc(docRef, {
    displayNameLower: displayName.toLowerCase(),
  });
}
